import { useCallback, useMemo, useState } from "react";

import _ from "lodash";

import { queryIsLoading, useGetPracticeMineralFertilizers, useGetPracticeOrganicFertilizerApplications, useGetPracticeOrganicFertilizers } from "utils";

import { FertilizerApplication, FertilizerType, PracticeFCPO } from "models";

import { Form } from "components";

import { BLANK_FERTILIZER_APPLICATION } from "pages/app/all-field-view/constants/fertilizerForm";
import { determinePracticeSettings } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { PracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";

export const useFertilizerForm = (
  chosenFertilizerType: FertilizerType,
  existingFertilizers: PracticeFCPO[],
  disabled: boolean,
  practiceConfigForBulk?: PracticeConfigForBulk
) => {
  const form = Form.useFormInstance();
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const fertilizerApplicationPracticesQuery = useGetPracticeOrganicFertilizerApplications({ enabled: true });

  const { defaultToggleState, existingPractices, toggleDisabled, hasMixedValues } = useMemo(
    () =>
      determinePracticeSettings({
        practiceConfigForBulk: practiceConfigForBulk,
        practices: existingFertilizers,
        disabled,
      }),
    [practiceConfigForBulk, existingFertilizers, disabled]
  );

  const [isFertilizerTypeApplied, setIsFertilizerTypeApplied] = useState<boolean | null>(defaultToggleState);
  const [fertilizerApplications, setFertilizerApplications] = useState(() => existingPractices);

  const handleFertilizerTypeChange = useCallback(
    (value: boolean) => {
      setIsFertilizerTypeApplied(() => {
        // if the first application is blank and the user chooses "Yes", add a new fertilizer application.
        if (fertilizerApplications.length === 0 && value === true) {
          setFertilizerApplications([BLANK_FERTILIZER_APPLICATION(chosenFertilizerType)]);
        }
        return value;
      });
    },
    [chosenFertilizerType, fertilizerApplications.length]
  );

  const handleRemoveFertilizerApplication = (index: number) => {
    setFertilizerApplications((prev) => {
      // if the last application is removed, set the state to null so as to close the radio group.
      // Doing this because React batch state updates and the state is not updated until the next rendering cycle.

      const updated = [...prev];
      updated.splice(index, 1);

      // update form
      const values = form.getFieldsValue(true);
      delete values[`${chosenFertilizerType}-${index}`];
      form.setFieldsValue(values);

      // update UI
      if (updated.length === 0) {
        form.setFieldValue(`is${_.upperFirst(chosenFertilizerType)}Applied`, null);
        setIsFertilizerTypeApplied(null);
      }
      return updated;
    });
  };

  const addNewFertilizerApplication = useCallback(() => {
    setFertilizerApplications((prev) => [...prev, BLANK_FERTILIZER_APPLICATION(chosenFertilizerType)]);
  }, [chosenFertilizerType]);

  const updateFertilizerApplication = useCallback((index: number, values: Partial<FertilizerApplication>) => {
    setFertilizerApplications((prev) => prev.map((app, i) => (i === index ? { ...app, ...values } : app)));
  }, []);

  return {
    isFertilizerTypeApplied,
    fertilizerApplications,
    handleFertilizerTypeChange,
    handleRemoveFertilizerApplication,
    addNewFertilizerApplication,
    updateFertilizerApplication,
    toggleDisabled,
    hasMixedValues,
    fertilizers: {
      applications: fertilizerApplicationPracticesQuery.data ?? [],
      mineral: mineralFertilizerPracticesQuery.data ?? [],
      organic: organicFertilizerPracticesQuery.data ?? [],
    },
    isFertilizerLoading: [mineralFertilizerPracticesQuery, organicFertilizerPracticesQuery, fertilizerApplicationPracticesQuery].some(queryIsLoading),
  };
};
